import axios from 'axios';
import { authService } from './authService';

/**
 * Serviço para enviar logs de erro do console para o backend
 * Fire-and-forget com deduplicação para evitar flood de requests
 */
class ErrorLogService {
  constructor() {
    this.apiUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}/console/errors`;
    this.maxRetries = 1;
    this.retryDelayMs = 500;
    this._sending = false;          // Evita requests simultâneos
    this._recentErrors = new Map(); // Deduplicação: mensagem → timestamp
    this._deduplicationWindowMs = 5000; // 5s: mesmo erro não é reenviado
  }

  /**
   * Envia um erro para o backend
   * Falha silenciosamente se houver erro
   * @param {Error | string} error - Erro a ser logado
   * @param {string} pageUrl - URL da página (opcional)
   * @param {object} context - Contexto extra opcional (componentStack, detectedExtensions, etc.)
   */
  async logError(error, pageUrl = null, context = null) {
    try {
      // Extrair mensagem e stack
      const message = typeof error === 'string' ? error : error?.message || String(error);
      const stack = typeof error === 'string' ? undefined : error?.stack;
      const key = message || 'Unknown error';

      // Deduplicação: não envia o mesmo erro em menos de 5s
      const now = Date.now();
      const lastSent = this._recentErrors.get(key);
      if (lastSent && now - lastSent < this._deduplicationWindowMs) {
        return;
      }
      this._recentErrors.set(key, now);

      // Limpar entradas antigas do mapa de deduplicação (evitar leak)
      if (this._recentErrors.size > 50) {
        for (const [k, ts] of this._recentErrors) {
          if (now - ts > this._deduplicationWindowMs) this._recentErrors.delete(k);
        }
      }

      // Não enviar se já há um request em andamento
      if (this._sending) return;

      // Preparar payload
      const payload = {
        message: key,
        stack: stack,
        pageUrl: pageUrl || window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        appVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null,
        ...(context ? { context } : {}),
      };

      // Fazer request com retry
      await this.sendWithRetry(payload);
    } catch (err) {
      // Falha silenciosa - não loga para não criar loop infinito
    }
  }

  /**
   * Envia a requisição com retry automático
   * @private
   */
  async sendWithRetry(payload, attempt = 0) {
    this._sending = true;
    try {
      // Verificar se está autenticado antes de enviar
      const token = authService.getToken();
      if (!token) {
        return;
      }

      await axios.post(this.apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        timeout: 5000,
      });
      // Sucesso (2xx) — nada a fazer
    } catch (err) {
      // Só retry em erros de REDE (timeout, connection refused), NÃO em respostas HTTP
      // err.response existe → servidor respondeu com 4xx/5xx → não retry
      const isNetworkError = !err.response;
      if (isNetworkError && attempt < this.maxRetries) {
        await new Promise((resolve) =>
          setTimeout(resolve, this.retryDelayMs * (attempt + 1))
        );
        // Libera flag antes de recursar para não bloquear indefinidamente
        this._sending = false;
        return this.sendWithRetry(payload, attempt + 1);
      }
      // HTTP error ou retries esgotados → falha silenciosa
    } finally {
      this._sending = false;
    }
  }
}

export const errorLogService = new ErrorLogService();
